/**
 * Tüketici API istemcisi. Taban `/api` (aynı kaynak — nginx api'ye yönlendirir; CORS yok).
 * İşletme `x-tenant-slug` başlığıyla, üye oturumu Bearer token ile taşınır.
 */
export const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api';

/** Sunucu hatası — HTTP durum + backend kodu + ek alanlar. */
export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string,
    public details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
  get isNetwork(): boolean {
    return this.status === 0;
  }
}

export function errorMessage(e: unknown, fallback = 'Bir şeyler ters gitti. Tekrar dene.'): string {
  if (e instanceof ApiError) {
    if (e.isNetwork) return 'Bağlantı yok. İnternetini kontrol edip tekrar dene.';
    if (e.status === 429) return e.message || 'Çok hızlı denedin. Biraz bekleyip tekrar dene.';
    return e.message || fallback;
  }
  if (e instanceof Error && e.message) return e.message;
  return fallback;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  anonymous?: boolean;
  headers?: Record<string, string>;
  timeoutMs?: number;
  signal?: AbortSignal;
}

interface ClientState {
  slug: string | null;
  token: string | null;
  onUnauthorized: (() => void) | null;
  onMenuVersion: ((v: number) => void) | null;
}

const state: ClientState = { slug: null, token: null, onUnauthorized: null, onMenuVersion: null };
let lastMenuVersion: number | null = null;

export function configureClient(patch: Partial<ClientState>): void {
  Object.assign(state, patch);
}

export async function apiFetch<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (opts.body !== undefined) headers['content-type'] = 'application/json';
  if (state.slug) headers['x-tenant-slug'] = state.slug;
  if (!opts.anonymous && state.token) headers.authorization = `Bearer ${state.token}`;
  Object.assign(headers, opts.headers ?? {});

  const ctrl = new AbortController();
  const timer = window.setTimeout(() => ctrl.abort(), opts.timeoutMs ?? 15_000);
  opts.signal?.addEventListener('abort', () => ctrl.abort(), { once: true });
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: opts.method ?? 'GET',
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: ctrl.signal,
      credentials: 'omit',
    });
  } catch (e) {
    const aborted = e instanceof DOMException && e.name === 'AbortError';
    throw new ApiError(0, aborted ? 'İstek zaman aşımına uğradı.' : 'Sunucuya ulaşılamadı.', 'NETWORK');
  } finally {
    window.clearTimeout(timer);
  }

  const mv = res.headers.get('x-menu-version');
  if (mv && Number.isFinite(Number(mv))) {
    const v = Number(mv);
    if (lastMenuVersion !== null && v !== lastMenuVersion) state.onMenuVersion?.(v);
    lastMenuVersion = v;
  }

  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }

  if (!res.ok) {
    const body = (data ?? {}) as Record<string, unknown>;
    const inner = typeof body.message === 'object' && body.message ? (body.message as Record<string, unknown>) : null;
    const message = (inner?.message as string) ?? (typeof body.message === 'string' ? body.message : `HTTP ${res.status}`);
    const code = (inner?.code as string) ?? (body.code as string | undefined);
    // Oturum yalnız KODSUZ 401'de düşer (token yok/geçersiz). PIN_INVALID / OTP_INVALID gibi kodlu 401 alan hatasıdır.
    if (res.status === 401 && !opts.anonymous && !code) state.onUnauthorized?.();
    const { statusCode: _s, message: _m, code: _c, path: _p, requestId: _r, timestamp: _t, ...rest } = body;
    throw new ApiError(res.status, typeof message === 'string' ? message : 'İstek başarısız', code, rest);
  }
  return data as T;
}

/** API görsel yolu: yükleme servisi zaten `/api/uploads/...` döner (aynı kaynak) — olduğu gibi kullanılır. */
export function assetUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  if (/^(https?:|data:|blob:|\/)/i.test(url)) return url;
  return `${API_BASE}/${url}`;
}

/** Idempotency anahtarı (sipariş/rezervasyon): timeout sonrası aynı anahtarla tekrar güvenli. */
export function requestId(prefix: string): string {
  const rnd = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${prefix}-${rnd}`;
}
